<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { type GoToItem } from '../../types';
import { type HighlightWithText } from '../../stores/highlights';
import useHighlightsStore from '../../stores/highlights';
import useUiStore from '../../stores/ui';
import AnnotationListItemBase from '../annotations/AnnotationListItemBase.vue';
import useMediaStore from '../../stores/media';

type Emits = {
  (e: 'goToAnnotation', item: GoToItem): void;
  (e: 'edit', highlight: HighlightWithText): void;
  (e: 'remove', highlight: HighlightWithText): void;
};
const emit = defineEmits<Emits>();

type Props = {
  highlight: HighlightWithText;
  isSaving?: boolean;
};
const props = defineProps<Props>();

const { toggleTableOfContentDrawer, toggleAnnotationsDrawer } = useUiStore();
const { smAndDown: isMobile } = useDisplay();
const { playing } = storeToRefs(useMediaStore());
const { getColor } = useHighlightsStore();

const isUnderline = computed(() => (props.highlight.type as string) === 'underline');

const highlightColor = computed(() => getColor(props.highlight.color || 'pink'));

const underlineColor = computed(() => getColor('red'));

// Calculate relative luminance for WCAG contrast
const getBrightness = (hexColor: string): number => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (perceived brightness)
  // Using the formula: 0.299*R + 0.587*G + 0.114*B
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

// Compute text color for highlights based on background brightness
const textColor = computed(() => {
  if (isUnderline.value) return 'inherit'; // Underlines don't need text color override

  const brightness = getBrightness(highlightColor.value);

  // Use white text for dark backgrounds, black for light backgrounds
  // Threshold at 128 (midpoint of 0-255 range)
  return brightness > 128 ? '#000000' : '#FFFFFF';
});

const goToHighlight = () => {
  if (isMobile.value) {
    setTimeout(() => toggleTableOfContentDrawer(false), 300);
    setTimeout(() => toggleAnnotationsDrawer(false), 300);
  }
  const item: GoToItem = { target: props.highlight.range, isGoingForwardsInBook: true };
  playing.value = false;

  emit('goToAnnotation', item);
};
</script>

<template>
  <AnnotationListItemBase
    :is-saving="isSaving"
    :data-testid="`bp-highlight-item-${highlight.id}`"
    @navigate="goToHighlight"
    @edit="emit('edit', highlight)"
    @remove="emit('remove', highlight)"
  >
    <v-list-item-media>
      <div class="label">
        <span class="highlight-text" :class="{ 'underlined-text': isUnderline }">
          {{ highlight.text }}
        </span>
      </div>
    </v-list-item-media>
    <v-list-item-subtitle v-if="highlight.comment?.type === 'text'">
      {{ highlight.comment.content }}
    </v-list-item-subtitle>
  </AnnotationListItemBase>
</template>

<style scoped lang="scss">
.label {
  line-clamp: 3;
  display: inline-flex;
  align-items: center;
}

.highlight-text {
  /* For highlights: background color with padding and dynamic text color */
  background-color: v-bind(highlightColor);
  color: v-bind(textColor);
  padding: 2px 6px;
  border-radius: 3px;
  display: inline;
  line-height: 1.6;
}

.underlined-text {
  /* For underlines: no background, red underline decoration */
  background-color: transparent;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: v-bind('underlineColor');
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
  padding: 0;
}
</style>
